// ============================================
// REVIEWS MODERATION PAGE
// i18n: Faz 6D (v2.6)
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import {
  Check,
  X,
  Star,
  Building2,
  Calendar,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import type { CompanyReview } from '@crew-at-sea/shared-types';
import {
  PageHeader,
  FilterBar,
  FilterSelect,
  ConfirmDialog,
  EmptyState,
} from '@/components';
import { adminApi } from '@/services/api';
import { formatRelativeTime, getInitials, cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/reviews/')({
  component: ReviewsPage,
});

function ReviewsPage() {
  const { t } = useTranslation(['panel', 'common']);
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    review: CompanyReview | null;
    action: 'approve' | 'reject';
  }>({ open: false, review: null, action: 'approve' });

  // Değerlendirmeleri getir
  const { data: reviewsData, isLoading } = useQuery({
    queryKey: ['admin', 'reviews', statusFilter],
    queryFn: () => adminApi.getReviews(statusFilter || 'all'),
  });

  // Onaylama mutation
  const approveMutation = useMutation({
    mutationFn: (id: string) => adminApi.approveReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      setConfirmDialog({ open: false, review: null, action: 'approve' });
    },
  });

  const reviews = Array.isArray(reviewsData) ? reviewsData : ((reviewsData as any)?.data || reviewsData || []);

  // Reddetme mutation
  const rejectMutation = useMutation({
    mutationFn: (id: string) => adminApi.rejectReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      setConfirmDialog({ open: false, review: null, action: 'reject' });
    },
  });

  const handleAction = () => {
    if (!confirmDialog.review) return;
    if (confirmDialog.action === 'approve') {
      approveMutation.mutate(confirmDialog.review.id);
    } else {
      rejectMutation.mutate(confirmDialog.review.id);
    }
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            'h-4 w-4',
            star <= rating
              ? 'text-amber-400 fill-current'
              : 'text-gray-300'
          )}
        />
      ))}
    </div>
  );

  const statusOptions = [
    { value: 'pending', label: t('panel:reviews.statusPending') },
    { value: 'approved', label: t('panel:reviews.statusApproved') },
    { value: 'rejected', label: t('panel:reviews.statusRejected') },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:reviews.title')}
        description={t('panel:reviews.description')}
      />

      <FilterBar
        filters={
          <FilterSelect
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            placeholder={t('common:status')}
          />
        }
      />

      {isLoading ? (
        <div className="grid gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white rounded-maritime border border-gray-200 p-6 animate-pulse"
            >
              <div className="h-6 w-1/3 bg-gray-200 rounded mb-4" />
              <div className="h-4 w-full bg-gray-200 rounded mb-2" />
              <div className="h-4 w-2/3 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      ) : reviews && reviews.length > 0 ? (
        <div className="grid gap-6">
          {reviews.map((review: CompanyReview & { company: { name: string }; seafarer: { firstName: string; lastName: string } }) => (
            <ReviewCard
              key={review.id}
              review={review}
              onApprove={() =>
                setConfirmDialog({ open: true, review, action: 'approve' })
              }
              onReject={() =>
                setConfirmDialog({ open: true, review, action: 'reject' })
              }
              renderStars={renderStars}
              t={t}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-maritime border border-gray-200">
          <EmptyState
            title={t('panel:reviews.noPendingReviews')}
            description={t('panel:reviews.allReviewed')}
          />
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={
          confirmDialog.action === 'approve'
            ? t('panel:reviews.approveTitle')
            : t('panel:reviews.rejectTitle')
        }
        description={
          confirmDialog.action === 'approve'
            ? t('panel:reviews.approveDescription')
            : t('panel:reviews.rejectDescription')
        }
        variant={confirmDialog.action === 'approve' ? 'success' : 'danger'}
        confirmText={confirmDialog.action === 'approve' ? t('panel:reviews.approve') : t('panel:reviews.reject')}
        onConfirm={handleAction}
        loading={approveMutation.isPending || rejectMutation.isPending}
      />
    </div>
  );
}

// Review Card Component
interface ReviewCardProps {
  review: CompanyReview & {
    company: { name: string };
    seafarer: { firstName: string; lastName: string };
  };
  onApprove: () => void;
  onReject: () => void;
  renderStars: (rating: number) => React.ReactNode;
  t: (key: string) => string;
}

function ReviewCard({ review, onApprove, onReject, renderStars, t }: ReviewCardProps) {
  return (
    <div className="bg-white rounded-maritime border border-gray-200 p-6">
      {/* Başlık */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-maritime-100 text-maritime-700 font-bold">
            {review.isAnonymous
              ? '?'
              : getInitials(review.seafarer.firstName, review.seafarer.lastName)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900">
                {review.isAnonymous
                  ? t('panel:reviews.anonymousUser')
                  : `${review.seafarer.firstName} ${review.seafarer.lastName}`}
              </p>
              {review.isAnonymous && (
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                  {t('panel:reviews.anonymous')}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {review.position} • {review.employmentYear}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <Calendar className="h-4 w-4" />
          {formatRelativeTime(review.createdAt)}
        </div>
      </div>

      {/* Şirket */}
      <div className="flex items-center gap-2 mb-4 text-gray-600">
        <Building2 className="h-4 w-4" />
        <span>{review.company.name}</span>
      </div>

      {/* Puanlamalar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 p-4 bg-gray-50 rounded-maritime">
        <div>
          <p className="text-xs text-gray-500 mb-1">{t('panel:reviews.ratingOverall')}</p>
          {renderStars(review.overallRating)}
        </div>
        {review.salaryRating && (
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('panel:reviews.ratingSalary')}</p>
            {renderStars(review.salaryRating)}
          </div>
        )}
        {review.workConditionRating && (
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('panel:reviews.ratingWorkCondition')}</p>
            {renderStars(review.workConditionRating)}
          </div>
        )}
        {review.managementRating && (
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('panel:reviews.ratingManagement')}</p>
            {renderStars(review.managementRating)}
          </div>
        )}
        {review.foodRating && (
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('panel:reviews.ratingFood')}</p>
            {renderStars(review.foodRating)}
          </div>
        )}
      </div>

      {/* Başlık */}
      {review.title && (
        <h4 className="font-semibold text-gray-900 mb-2">{review.title}</h4>
      )}

      {/* Artılar & Eksiler */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        {review.pros && (
          <div>
            <div className="flex items-center gap-1 text-green-600 text-sm font-medium mb-1">
              <ThumbsUp className="h-4 w-4" />
              {t('panel:reviews.pros')}
            </div>
            <p className="text-sm text-gray-600">{review.pros}</p>
          </div>
        )}
        {review.cons && (
          <div>
            <div className="flex items-center gap-1 text-red-600 text-sm font-medium mb-1">
              <ThumbsDown className="h-4 w-4" />
              {t('panel:reviews.cons')}
            </div>
            <p className="text-sm text-gray-600">{review.cons}</p>
          </div>
        )}
      </div>

      {/* Aksiyonlar */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          onClick={onReject}
          className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-maritime transition-colors"
        >
          <X className="h-4 w-4" />
          {t('panel:reviews.reject')}
        </button>
        <button
          onClick={onApprove}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-maritime transition-colors"
        >
          <Check className="h-4 w-4" />
          {t('panel:reviews.approve')}
        </button>
      </div>
    </div>
  );
}
